/**
 * 来場者セッション復元フック
 * URL状態またはlocalStorageからセッショントークンを復元します
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { clearVisitorSession, loadVisitorSession } from '../services/visitorSessionService';
import { createLoginReturnDestination } from '../utils/loginReturnDestination';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import { logError } from '../../../shared/utils/logger';

/**
 * ログイン画面へ渡すパラメータを作る
 * QRで開いた画面なら、ログイン後にQRの続きへ戻れるよう戻り先を付ける。
 * @param {Object | undefined} route - 開いている画面のルート
 * @param {boolean} isSessionLost - ログイン状態が消えたことを案内するか
 * @returns {{ sessionLost: boolean, returnTo?: import('../utils/loginReturnDestination').LoginReturnDestination }} ログイン画面のパラメータ
 */
const buildLoginParams = (route, isSessionLost) => {
  /** QRで開いた画面の戻り先 */
  const returnTo = createLoginReturnDestination(route?.name, route?.params);

  return returnTo ? { sessionLost: isSessionLost, returnTo } : { sessionLost: isSessionLost };
};

/**
 * URL状態またはlocalStorageからセッショントークンとvisitorIdを取得するフック
 * @param {Object} navigation - ナビゲーション
 * @param {Object} route - ルート
 * @returns {{ sessionToken: string, visitorId: string, isSessionReady: boolean, handleSessionLost: () => Promise<void> }} セッション情報
 */
const useVisitorSession = (navigation, route) => {
  /** route経由のセッショントークン */
  const routeSessionToken = route?.params?.sessionToken ?? '';

  /** route経由の来場者ID */
  const routeVisitorId = route?.params?.visitorId ?? '';

  /** セッショントークン */
  const [sessionToken, setSessionToken] = useState(routeSessionToken);

  /** 来場者ID */
  const [visitorId, setVisitorId] = useState(routeVisitorId);

  /** セッション準備完了フラグ */
  const [isSessionReady, setIsSessionReady] = useState(!!routeSessionToken);

  /** 最新のルート（handleSessionLostの参照を変えずにQRの値を読むため） */
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    // route paramsにトークンがあればそのまま使用
    if (routeSessionToken) {
      setSessionToken(routeSessionToken);
      if (routeVisitorId) {
        setVisitorId(routeVisitorId);
      }
      setIsSessionReady(true);
      return;
    }

    let isActive = true;

    /**
     * localStorageから保存済みセッションを復元する
     * @returns {Promise<void>}
     */
    const restoreFromStorage = async () => {
      try {
        /** 保存済みセッション */
        const storedSession = await loadVisitorSession();

        if (!isActive) {
          return;
        }

        if (!storedSession?.sessionToken) {
          // セッション情報なし → ログイン画面へ。
          // QRで開いた場合は、カメラやLINEがアプリと別のブラウザで開いただけのことが多いため、
          // ログイン状態の消失としては案内せず、QRの値を持ってログイン後に戻る。
          /** QRで開いた画面の戻り先 */
          const returnTo = createLoginReturnDestination(routeRef.current?.name, routeRef.current?.params);
          navigation.reset({
            index: 0,
            routes: [{
              name: VISITOR_ROUTES.LOGIN,
              params: returnTo ? { sessionLost: false, returnTo } : { sessionLost: true },
            }],
          });
          return;
        }

        setSessionToken(storedSession.sessionToken);
        if (storedSession.visitorId) {
          setVisitorId(storedSession.visitorId);
        }
        setIsSessionReady(true);
      } catch (error) {
        logError('セッション復元に失敗しました。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });

        if (!isActive) {
          return;
        }

        // エラー時もログイン画面へ
        try {
          await clearVisitorSession();
        } catch (clearError) {
          logError('セッション削除に失敗しました。', {
            errorMessage: clearError instanceof Error ? clearError.message : String(clearError),
          });
        }

        navigation.reset({
          index: 0,
          routes: [{ name: VISITOR_ROUTES.LOGIN, params: buildLoginParams(routeRef.current, true) }],
        });
      }
    };

    restoreFromStorage();

    return () => {
      isActive = false;
    };
  }, [navigation, routeSessionToken]);

  /**
   * セッション喪失時にログイン画面へ遷移する
   * @returns {Promise<void>}
   */
  const handleSessionLost = useCallback(async () => {
    try {
      await clearVisitorSession();
    } catch (error) {
      logError('セッション削除に失敗しました。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }

    navigation.reset({
      index: 0,
      routes: [{ name: VISITOR_ROUTES.LOGIN, params: buildLoginParams(routeRef.current, true) }],
    });
  }, [navigation]);

  return { sessionToken, visitorId, isSessionReady, handleSessionLost };
};

export default useVisitorSession;
